import { Info, TriangleAlert } from "lucide-react";
import {
  Form,
  maxValue,
  minValue,
  required,
  useCreate,
  useGetIdentity,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { LifeWheel, LifeWheelScores } from "../types";
import { LIFE_WHEEL_MAX_SCORE, LIFE_WHEEL_TOPICS } from "./lifeWheelTopics";

const RESOURCE = "life_wheel";
const scoreValidators = [
  required(),
  minValue(0, "crm.personal.life_wheel.invalid_score"),
  maxValue(LIFE_WHEEL_MAX_SCORE, "crm.personal.life_wheel.invalid_score"),
];

export interface LifeWheelDialogProps {
  open: boolean;
  onClose: () => void;
  lifeWheel?: LifeWheel | null;
}

const toCompleteScores = (scores: LifeWheelScores = {}): LifeWheelScores =>
  Object.fromEntries(
    LIFE_WHEEL_TOPICS.map((topic) => [
      topic,
      {
        current: scores[topic]?.current ?? 0,
        target: scores[topic]?.target ?? 0,
      },
    ]),
  );

export const LifeWheelDialog = ({
  open,
  onClose,
  lifeWheel,
}: LifeWheelDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create] = useCreate();
  const [update] = useUpdate();

  const record = useMemo(
    () => ({ scores: toCompleteScores(lifeWheel?.scores) }),
    [lifeWheel],
  );

  const handleSubmit = async (values: FieldValues) => {
    const data = {
      scores: toCompleteScores(values.scores),
      updated_at: new Date().toISOString(),
    };
    const options = {
      onSuccess: () => {
        notify("crm.personal.life_wheel.saved");
        onClose();
      },
      onError: (error: unknown) =>
        notify(
          error instanceof Error ? error.message : "ra.notification.http_error",
          { type: "error" },
        ),
    };
    if (lifeWheel) {
      await update(
        RESOURCE,
        { id: lifeWheel.id, data, previousData: lifeWheel },
        options,
      );
      return;
    }
    await create(
      RESOURCE,
      { data: { ...data, sales_id: identity?.id } },
      options,
    );
  };

  const currentLabel = translate("crm.personal.life_wheel.current");
  const targetLabel = translate("crm.personal.life_wheel.target");

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="lg:max-w-xl overflow-y-auto max-h-9/10">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate("crm.personal.life_wheel.title")}
              </DialogTitle>
              <DialogDescription>
                {translate("crm.personal.life_wheel.dialog_description")}
              </DialogDescription>
            </DialogHeader>

            <Alert>
              <TriangleAlert />
              <AlertDescription>
                {translate("crm.personal.life_wheel.zero_warning")}
              </AlertDescription>
            </Alert>

            <TooltipProvider delayDuration={150}>
              <div className="grid grid-cols-[1fr_88px_88px] items-center gap-x-3 gap-y-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {translate("crm.personal.life_wheel.area")}
                </span>
                <span className="text-center text-xs font-medium text-muted-foreground">
                  {currentLabel}
                </span>
                <span className="text-center text-xs font-medium text-muted-foreground">
                  {targetLabel}
                </span>

                {LIFE_WHEEL_TOPICS.map((topic) => {
                  const name = translate(
                    `crm.personal.life_wheel.topics.${topic}.name`,
                  );
                  return (
                    <div key={topic} className="contents">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex w-fit cursor-help items-center gap-1.5 text-sm">
                            {name}
                            <Info className="size-3.5 text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-64">
                          {translate(
                            `crm.personal.life_wheel.topics.${topic}.description`,
                          )}
                        </TooltipContent>
                      </Tooltip>
                      <NumberInput
                        source={`scores.${topic}.current`}
                        label={false}
                        aria-label={`${name} – ${currentLabel}`}
                        min={0}
                        max={LIFE_WHEEL_MAX_SCORE}
                        step={1}
                        validate={scoreValidators}
                        className="text-center"
                      />
                      <NumberInput
                        source={`scores.${topic}.target`}
                        label={false}
                        aria-label={`${name} – ${targetLabel}`}
                        min={0}
                        max={LIFE_WHEEL_MAX_SCORE}
                        step={1}
                        validate={scoreValidators}
                        className="text-center"
                      />
                    </div>
                  );
                })}
              </div>
            </TooltipProvider>

            <DialogFooter>
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
